import { useCallback, useState } from 'react';

import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import {
  useCreateRequestEventMutation,
  useSearchRequestQuery,
} from '@/app/services/issues';
import { addToast } from '@/app/slices/toast';
import { ToastLife } from '@/components/hoc/toast-container/enums';
import {
  RequestEventFragment,
  RequestEventType,
  RequestFragment,
} from '@/rtk-query.gql';

import { ICONS } from './constants';

type FormValues = {
  refRequestId: number;
  searchQuery: string;
};

type MarkAsDuplicateProps = {
  request: RequestFragment & { events: RequestEventFragment[] };
  canManageRequest: (
    request: RequestFragment & { events: RequestEventFragment[] },
  ) => boolean;
};

export const MarkAsDuplicate = ({
  request,
  canManageRequest,
}: MarkAsDuplicateProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchQuery, setSearchQuery] = useState('');
  const [findingDuplicate, setFindingDuplicate] = useState(false);

  const [createEvent, { isLoading }] = useCreateRequestEventMutation();
  const { data: searchResults } = useSearchRequestQuery({
    query: searchQuery,
    type: request.type,
  });

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>();

  const onSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await createEvent({
          type: RequestEventType.DENIED,
          content: 'DUPLICATE',
          requestId: request.id,
          refRequestId: values.refRequestId,
        });

        dispatch(
          addToast({
            severity: 'success',
            summary: t('common.success'),
            detail: t('issues.deniedIssueAsDuplicate'),
            life: ToastLife.DEFAULT,
          }),
        );
      } catch (error) {
        dispatch(
          addToast({
            severity: 'error',
            summary: t('common.error'),
            detail: t('issues.errorMarkingAsDuplicate') + error,
            life: ToastLife.DEFAULT,
          }),
        );
      }
    },
    [createEvent, dispatch, request.id, t],
  );

  const startProcess = useCallback(() => setFindingDuplicate(true), []);

  const cancelProcess = useCallback(() => {
    setFindingDuplicate(false);
    reset();
    setSearchQuery('');
  }, [reset]);

  if (!canManageRequest(request)) {
    return null;
  }

  if (findingDuplicate) {
    return (
      <form className="duplicate" onSubmit={handleSubmit(onSubmit)}>
        <div className="duplicate__field p-fluid">
          <InputText
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('issues.searchForRequest')}
          />
        </div>

        <div className="duplicate__field p-fluid">
          <Controller
            name="refRequestId"
            control={control}
            rules={{ required: t('issues.requestRequired') }}
            render={({ field, fieldState }) => (
              <Dropdown
                id={field.name}
                {...field}
                className={classNames({ 'p-invalid': fieldState.error })}
                options={searchResults}
                placeholder={t('issues.selectRequest')}
                optionValue="id"
                optionLabel="label"
                valueTemplate={(item) =>
                  item ? `#${item.id}` : t('issues.selectRequest')
                }
                itemTemplate={(item) =>
                  item ? `#${item.id} ${item.title}` : ''
                }
              />
            )}
          />
          {errors.refRequestId && (
            <small className="p-error">{errors.refRequestId.message}</small>
          )}
        </div>

        <div className="duplicate__buttons p-buttonset">
          <Button
            type="submit"
            label={t('issues.denyAsDuplicate')}
            className="status-button status-button--denied"
            icon={ICONS[RequestEventType.DENIED]}
            disabled={isLoading}
            loading={isLoading}
          />
          <Button
            type="button"
            label={t('common.cancel')}
            severity="secondary"
            disabled={isLoading}
            onClick={cancelProcess}
          />
        </div>
      </form>
    );
  }

  return (
    <Button
      label={t('issues.denyAsDuplicate')}
      className="status-button status-button--denied"
      icon={ICONS[RequestEventType.DENIED]}
      onClick={startProcess}
    />
  );
};
